import React, { useState, useRef, useCallback } from 'react';
import { UploadedImage } from '../types';

interface ImageUploadProps {
    onImageUpload: (image: UploadedImage) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ onImageUpload }) => {
    const [dragOver, setDragOver] = useState(false);
    const [selectedImage, setSelectedImage] = useState<UploadedImage | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback((file: File) => {
        if (file.type.startsWith('image/')) {
            const previewUrl = URL.createObjectURL(file);
            const imageData: UploadedImage = {
                file,
                previewUrl
            };
            setSelectedImage(imageData);
            onImageUpload(imageData);
        }
    }, [onImageUpload]);

    const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(false);
        
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = () => {
        setDragOver(false);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveImage = () => {
        if (selectedImage) {
            URL.revokeObjectURL(selectedImage.previewUrl);
        }
        setSelectedImage(null);
    };

    return (
        <div className="image-upload">
            <h3>Upload Your Image</h3>
            
            {!selectedImage ? (
                <div
                    className={`upload-area ${dragOver ? 'dragover' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleClick}
                >
                    <div className="upload-icon">📷</div>
                    <div className="upload-text">
                        Click to upload or drag and drop
                    </div>
                    <div className="upload-subtext">
                        PNG, JPG, JPEG up to 10MB
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="file-input"
                    />
                </div>
            ) : (
                <div className="uploaded-image">
                    <img
                        src={selectedImage.previewUrl}
                        alt="Uploaded"
                        style={{
                            width: '100%',
                            maxHeight: '200px',
                            objectFit: 'contain',
                            borderRadius: '8px',
                            marginBottom: '16px'
                        }}
                    />
                    <div style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '12px' }}>
                            {selectedImage.file.name}
                        </p>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                                onClick={handleClick}
                                className="control-btn"
                                style={{ fontSize: '0.8rem' }}
                            >
                                Change Image
                            </button>
                            <button
                                onClick={handleRemoveImage}
                                className="control-btn"
                                style={{ 
                                    fontSize: '0.8rem',
                                    borderColor: '#ff4444',
                                    color: '#ff4444'
                                }}
                            >
                                Remove
                            </button>
                        </div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleFileInputChange}
                        className="file-input"
                    />
                </div>
            )}
        </div>
    );
};

export default ImageUpload;